/**
 * CW-SF-R2 TKT-6 — Bookings This Q vs Last Q widget (CEO role).
 *
 * Compact 2-up scalar: current fiscal Q closed-won vs prior Q + delta %.
 */
import { useCallback } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface QBucket {
  label: string;
  total: number;
  count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  current: QBucket | null;
  prior: QBucket | null;
  delta_pct: number | null;
}

const REFRESH_S = 5 * 60;

export function SfBookingsQoQWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/bookings-q-over-q', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const delta = data?.delta_pct;
  let DeltaIcon = Minus;
  let deltaCls = 'sf-widget__delta--flat';
  if (delta !== null && delta !== undefined) {
    if (delta > 0) { DeltaIcon = ArrowUp; deltaCls = 'sf-widget__delta--up'; }
    else if (delta < 0) { DeltaIcon = ArrowDown; deltaCls = 'sf-widget__delta--down'; }
  }

  return (
    <div className="sf-widget sf-widget--bookings-qoq">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={!!(data?.current || data?.prior)}
        emptyMessage="No bookings recorded yet."
      >
        <div className="sf-bookings-qoq">
          <div className="sf-bookings-qoq__cell">
            <div className="sf-bookings-qoq__label">{data?.current?.label ?? '—'}</div>
            <div className="sf-bookings-qoq__value">
              {formatCurrency(data?.current?.total ?? 0)}
            </div>
            <div className="sf-bookings-qoq__meta">
              {(data?.current?.count ?? 0)} won
            </div>
          </div>
          <div className={`sf-bookings-qoq__delta ${deltaCls}`}>
            <DeltaIcon size={16} />
            <span>{delta !== null && delta !== undefined ? `${delta > 0 ? '+' : ''}${delta}%` : '—'}</span>
          </div>
          <div className="sf-bookings-qoq__cell">
            <div className="sf-bookings-qoq__label">{data?.prior?.label ?? '—'}</div>
            <div className="sf-bookings-qoq__value sf-bookings-qoq__value--muted">
              {formatCurrency(data?.prior?.total ?? 0)}
            </div>
            <div className="sf-bookings-qoq__meta">
              {(data?.prior?.count ?? 0)} won
            </div>
          </div>
        </div>
      </SfWidgetShell>
    </div>
  );
}
